import { cn } from 'cn';
import { type ReactNode } from 'react';

import { slugify } from '@vestigas/utils';

type HeadingRowProps = React.PropsWithChildren<{
  action?: ReactNode;
  heading: string;
  headingClassName?: string;
  icon?: ReactNode;
  id?: string;
  variant?: 'h1' | 'h2' | 'h3' | 'h4';
}> &
  ComponentStyling;

/**
 * A heading row with optional additional functionality such as a CTA button.
 *
 * @component
 * @param action - The action element to be rendered on the right side of the heading row.
 * @param children - The additional content to be rendered below the heading row.
 * @param className - The CSS class name to be applied to the heading row container.
 * @param heading - The text to be displayed as the heading.
 * @param headingClassName - The CSS class name to be applied to the heading element.
 * @param icon - The icon element to be displayed before the heading text.
 * @param id - The HTML id attribute for the heading row container.
 * @param style - The inline CSS styles to be applied to the heading row container.
 * @param variant - The variant of the heading element ('h1', 'h2', 'h3', etc.). Defaults to 'h2'.
 *
 * @example
 * <HeadingRow
 *   action={<Button onClick={handleAction}>Action</Button>}
 *   heading="Heading Text"
 *   variant="h1"
 * >
 *   <p>Additional content</p>
 * </HeadingRow>
 *
 */
export const HeadingRow = ({
  action,
  children,
  className,
  heading,
  headingClassName,
  icon,
  id,
  style,
  variant = 'h2',
}: HeadingRowProps) => {
  const HeadingElement = variant;

  return (
    <header
      className={cn(
        '@container/heading-row relative',
        {
          'pl-1': variant === 'h1',
        },
        className,
      )}
      style={style}
    >
      <div
        id={id ?? slugify(heading)}
        className={cn(
          'group/heading-row flex flex-wrap items-center gap-2 @2xl/heading-row:flex-nowrap',
          {
            'py-8': variant === 'h1',
          },
        )}
      >
        <div className="flex max-w-full flex-2 items-center gap-4 text-ellipsis group-has-[[data-overflow-actions]]/heading-row:max-w-2/3 group-has-[[data-overflow-actions]]/heading-row:min-w-0 group-has-[[data-overflow-actions]]/heading-row:flex-initial">
          <HeadingElement
            className={cn(
              'order-1 line-clamp-1 flex items-center text-balance text-tiga-text-default',
              {
                'gap-2 text-2xl': variant === 'h2',
                'gap-2 text-base': variant === 'h4',
                'gap-2 text-xl': variant === 'h3',
                'gap-4 pb-[0.15em] text-4xl': variant === 'h1',
              },
              headingClassName,
            )}
          >
            {icon ? <span>{icon}</span> : null}
            <span className="w-full">{heading}</span>
          </HeadingElement>
        </div>
        {Boolean(children) && (
          <div className="order-4 flex w-full flex-none basis-full items-center lg:order-2 lg:w-auto lg:basis-0">
            {children}
          </div>
        )}
        {Boolean(action) && (
          <div className="order-3 flex flex-wrap items-center justify-end gap-x-2 gap-y-1 has-[[data-overflow-actions]]:min-w-0 has-[[data-overflow-actions]]:flex-1 has-[[data-overflow-actions]]:basis-0 @4xl/heading-row:flex-0 @4xl/heading-row:basis-0 @4xl/heading-row:flex-nowrap @4xl/heading-row:has-[[data-overflow-actions]]:flex-1">
            {action}
          </div>
        )}
      </div>
    </header>
  );
};
